import clsx from 'clsx';
import { UserPlusIcon } from '@heroicons/react/24/outline';
import { CheckCircleIcon, MinusCircleIcon } from '@heroicons/react/24/solid';
import { formatSlotDate } from '@/lib/localDate';
import type { SignupSlot, SignupEntry } from '@flock-types/signupSheets.types';
import EntryRow from './EntryRow';

interface Props {
  slot: SignupSlot;
  userId?: string;
  isLeader: boolean;
  isPast: boolean;
  editMode: boolean;
  busy: boolean;
  /** True while THIS slot's sign-up call is in flight. */
  signingUp: boolean;
  onSignUp: () => void;
  onWithdraw: () => void;
  onAssign: () => void;
  onEdit: () => void;
  onDeleteSlot: () => void;
  onRemoveEntry: (entry: SignupEntry) => void;
  onSaveNote: (entry: SignupEntry, note: string | null) => Promise<void>;
}

/** Sign-up slot row: status icon, date/label, capacity, actions, entries. */
export default function SlotRow({
  slot,
  userId,
  isLeader,
  isPast,
  editMode,
  busy,
  signingUp,
  onSignUp,
  onWithdraw,
  onAssign,
  onEdit,
  onDeleteSlot,
  onRemoveEntry,
  onSaveNote,
}: Props) {
  const entries = slot.entries || [];
  const filled = entries.length;
  const isFull = filled >= slot.capacity;
  const userEntry = entries.find((e) => e.user_id === userId);
  const clickToEdit = isLeader && !isPast && !editMode;

  return (
    <div
      onClick={clickToEdit ? onEdit : undefined}
      role={clickToEdit ? 'button' : undefined}
      className={clsx(
        'rounded-xl border border-flock-gray-200 bg-white p-4',
        isPast && 'opacity-50',
        clickToEdit && 'cursor-pointer transition hover:border-flock-navy-300'
      )}
    >
      <div className="flex items-center gap-2">
        {editMode && !isPast ? (
          <button
            onClick={(e) => {
              e.stopPropagation();
              onDeleteSlot();
            }}
            title="Remove slot"
            className="shrink-0 text-red-500 transition hover:text-red-600"
          >
            <MinusCircleIcon className="h-6 w-6" />
          </button>
        ) : isFull ? (
          <CheckCircleIcon className="h-5 w-5 shrink-0 text-emerald-500" />
        ) : (
          <span className="h-5 w-5 shrink-0 rounded-full border-2 border-flock-navy-300" />
        )}

        <span className="min-w-0 flex-1 truncate font-medium text-flock-navy-800">
          {slot.slot_date ? formatSlotDate(slot.slot_date) : ''}
          {slot.slot_date && slot.label ? ' — ' : ''}
          {slot.label || ''}
        </span>

        {slot.capacity > 1 && (
          <span className="shrink-0 text-xs text-flock-navy-400">
            {filled}/{slot.capacity}
          </span>
        )}

        {!isFull && !isPast && (
          <span className="flex shrink-0 items-center gap-2" onClick={(e) => e.stopPropagation()}>
            {!userEntry && (
              <button
                onClick={onSignUp}
                disabled={busy}
                className={clsx(
                  'rounded-lg px-3 py-1.5 text-sm font-medium text-white transition',
                  signingUp ? 'bg-flock-navy-400' : 'bg-flock-navy-600 hover:bg-flock-navy-700'
                )}
              >
                {signingUp ? 'Signing Up…' : 'Sign Up'}
              </button>
            )}
            {isLeader && (
              <button
                onClick={onAssign}
                className="flex items-center gap-1 rounded-lg bg-flock-navy-100 px-3 py-1.5 text-sm font-medium text-flock-navy-700 transition hover:bg-flock-navy-200"
              >
                <UserPlusIcon className="h-4 w-4" />
                Assign
              </button>
            )}
          </span>
        )}
      </div>

      {entries.length > 0 && (
        <div className="ml-7 mt-2" onClick={(e) => e.stopPropagation()}>
          {entries.map((entry) => (
            <EntryRow
              key={entry.id}
              entry={entry}
              isOwn={entry.user_id === userId}
              isLeader={isLeader}
              isPast={isPast}
              onRemove={() => (entry.user_id === userId ? onWithdraw() : onRemoveEntry(entry))}
              onSaveNote={(note) => onSaveNote(entry, note)}
            />
          ))}
        </div>
      )}
    </div>
  );
}
